class OffcanvasNav extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.offcanvasClose = this.querySelector(".offcanvas__inner--close");
    this.offcanvasNavWrapper = this.querySelector(".offcanvas-overlay");
    this.headerActions = document.querySelectorAll(".header__actions_btn");
    this.headerActionBtnMenu = this.querySelector(".header__actions_btn--menu");
    this.headerActionCart = document.querySelector(
      ".header__actions_btn--cart"
    );
    this.searchBox = document.querySelectorAll(".search--box-wrapper");

    // Mobile drawer redesign: Store references to drawer elements for portal functionality
    this.drawerOverlay = this.querySelector('.drawer__overlay');
    this.drawerInner = this.querySelector('.offcanvas__inner');
    this.isMobileDrawerRedesign = this.classList.contains('mobile-drawer-redesign');
    this.drawerMovedToBody = false;

    // Bind keyup handler to document for ESC key (since drawer may be moved to body)
    this.boundOnKeyUp = this.onKeyUp.bind(this);
    document.addEventListener("keyup", this.boundOnKeyUp);
    this.bindEvents();
  }

  bindEvents() {
    this.searchBox.forEach((actions) => {
      actions.addEventListener("click", (event) => {
        // Only close menu drawer if it's actually open
        if (this.mainDetailsToggle.hasAttribute("open") || this.drawerMovedToBody) {
          this.closeMenuDrawer(event);
        }
      });
    });
    this.headerActions.forEach((actions) => {
      actions.addEventListener("click", (event) => {
        event.stopPropagation();
        // Only close menu drawer if it's actually open
        if (this.mainDetailsToggle.hasAttribute("open") || this.drawerMovedToBody) {
          this.closeMenuDrawer(event);
        }
      });
    });
    this.querySelectorAll("summary").forEach((summary) => {
      summary.addEventListener("click", this.onSummaryClick.bind(this));
    });
    this.querySelectorAll(".offcanvas__inner--heading button").forEach(
      (button) =>
        button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
    this.headerActionBtnMenu.addEventListener("click", (event) => {
      setTimeout(() => {
        const headerActionHidden = document.querySelector(
          ".shopify-section-header-hidden"
        );
        if (headerActionHidden) {
          headerActionHidden?.classList.remove("shopify-section-header-hidden");
        }
      }, 10);
    });
    if (this.headerActionCart) {
      this.headerActionCart.addEventListener("click", (event) => {
        setTimeout(() => {
          const headerActionHidden = document.querySelector(
            ".shopify-section-header-hidden"
          );
          if (headerActionHidden) {
            headerActionHidden?.classList.remove("shopify-section-header-hidden");
          }
        }, 10);
      });
    }

    // Mobile drawer redesign: Close button handler
    // Store reference since it's inside drawerInner which may be moved
    this.drawerCloseButton = this.querySelector('.drawer__close-button');
    if (this.drawerCloseButton) {
      this.drawerCloseButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeMenuDrawer(event);
      });
    }

    // Mobile drawer redesign: Overlay click handler (use stored reference)
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeMenuDrawer(event);
      });
    }

  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    // Only process if drawer is open
    if (!this.mainDetailsToggle.hasAttribute("open") && !this.drawerMovedToBody) return;

    // Remove any submenu focus trap
    this.removeSubmenuFocusTrap();

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.closeAnimation(this.mainDetailsToggle);
    const submenuElement = document.querySelector(
      ".offcanvas__menu .submenu-open"
    );

    if (submenuElement) {
      submenuElement.classList.remove("submenu-open");
    };
    document.body.classList.remove("overflow-hidden");

    // Mobile drawer redesign: Animate out first, then move drawer elements back
    if (this.isMobileDrawerRedesign && this.drawerMovedToBody) {
      // Remove focus trap before closing
      this.removeFocusTrap();

      // Remove visible classes to trigger close animation
      this.drawerOverlay.classList.remove('drawer__overlay--visible');
      this.drawerInner.classList.remove('offcanvas__inner--visible');

      setTimeout(() => {
        this.moveDrawerBack();
      }, 450);
    } else {
      // For non-redesign mode, just remove focus trap
      this.removeFocusTrap();
    }
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has__sublist");
    const isOpen = detailsElement.hasAttribute("open");

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        document.body.classList.add("overflow-hidden");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");

        // Focus the back button in the newly opened submenu for accessibility
        const sublist = detailsElement.querySelector('.nav-menu__sublist');
        if (sublist) {
          const backButton = sublist.querySelector('.offcanvas__menu-close-button');
          if (backButton) {
            // Small delay to allow CSS transition to start
            setTimeout(() => {
              backButton.focus();
              // Set up focus trap for this submenu
              this.setupSubmenuFocusTrap(sublist);
            }, 100);
          }
        }
      }, 100);
    }
  }

  // Set up focus trap within a submenu
  setupSubmenuFocusTrap(sublist) {
    // Remove any existing submenu focus trap
    this.removeSubmenuFocusTrap();

    // Get all focusable elements within the submenu
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = sublist.querySelectorAll(focusableSelector);

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Store the handler so we can remove it later
    this.submenuKeydownHandler = (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab: if on first element, go to last
        if (document.activeElement === firstFocusable) {
          event.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab: if on last element, go to first
        if (document.activeElement === lastFocusable) {
          event.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    // Store reference to the sublist for cleanup
    this.currentSubmenuWithTrap = sublist;

    // Add the keydown listener to the sublist
    sublist.addEventListener('keydown', this.submenuKeydownHandler);
  }

  // Remove submenu focus trap
  removeSubmenuFocusTrap() {
    if (this.currentSubmenuWithTrap && this.submenuKeydownHandler) {
      this.currentSubmenuWithTrap.removeEventListener('keydown', this.submenuKeydownHandler);
      this.currentSubmenuWithTrap = null;
      this.submenuKeydownHandler = null;
    }
  }

  openMenuDrawer(summaryElement) {
    // Store reference to the element that opened the drawer for focus restoration
    this.openerElement = summaryElement;

    document.body.classList.add("overflow-hidden");
    this.header = this.header || document.querySelector(".header__area");
    document.documentElement.style.setProperty(
      "--header-height-from-top",
      `${this.header.getBoundingClientRect().bottom}px`
    );

    // Mobile drawer redesign: Move drawer elements to body to escape sticky header transform context
    if (this.isMobileDrawerRedesign && !this.drawerMovedToBody) {
      this.moveDrawerToBody();
    } else if (!this.isMobileDrawerRedesign && this.drawerInner) {
      // For non-redesign mode, set up focus trap after drawer opens
      setTimeout(() => {
        this.setupFocusTrap();
      }, 100);
    }

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
  }

  // Move drawer overlay and inner to body level to escape transformed containing block
  moveDrawerToBody() {
    if (this.drawerOverlay && this.drawerInner) {
      // Store original parent reference for restoration
      this.originalOverlayParent = this.drawerOverlay.parentElement;
      this.originalInnerParent = this.drawerInner.parentElement;
      this.originalOverlayNextSibling = this.drawerOverlay.nextSibling;
      this.originalInnerNextSibling = this.drawerInner.nextSibling;

      // Move to body
      document.body.appendChild(this.drawerOverlay);
      document.body.appendChild(this.drawerInner);
      this.drawerMovedToBody = true;

      // Trigger reflow to ensure transition works after DOM move
      this.drawerInner.offsetHeight;

      // Add visible classes after a small delay to trigger animation
      requestAnimationFrame(() => {
        this.drawerOverlay.classList.add('drawer__overlay--visible');
        this.drawerInner.classList.add('offcanvas__inner--visible');

        // Set up focus trap and focus the close button after animation starts
        setTimeout(() => {
          this.setupFocusTrap();
        }, 100);
      });
    }
  }

  // Set up focus trap within the drawer
  setupFocusTrap() {
    if (!this.drawerInner) return;

    // Get the first focusable element (close button) to focus
    const focusTarget = this.drawerCloseButton || this.drawerInner.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

    // Use global trapFocus if available, otherwise implement basic focus management
    if (typeof trapFocus === 'function') {
      trapFocus(this.drawerInner, focusTarget);
    } else {
      // Fallback: manually focus the first element
      if (focusTarget) {
        focusTarget.focus();
      }
    }
  }

  // Remove focus trap and restore focus
  removeFocusTrap() {
    // Determine which element to restore focus to
    const focusRestoreTarget = this.openerElement || this.headerActionBtnMenu;

    if (typeof removeTrapFocus === 'function') {
      // Restore focus to the element that opened the drawer
      removeTrapFocus(focusRestoreTarget);
    } else if (focusRestoreTarget) {
      focusRestoreTarget.focus();
    }
  }

  // Move drawer elements back to their original position
  moveDrawerBack() {
    if (this.drawerMovedToBody && this.originalOverlayParent && this.originalInnerParent) {
      // Restore overlay
      if (this.originalOverlayNextSibling) {
        this.originalOverlayParent.insertBefore(this.drawerOverlay, this.originalOverlayNextSibling);
      } else {
        this.originalOverlayParent.appendChild(this.drawerOverlay);
      }

      // Restore inner
      if (this.originalInnerNextSibling) {
        this.originalInnerParent.insertBefore(this.drawerInner, this.originalInnerNextSibling);
      } else {
        this.originalInnerParent.appendChild(this.drawerInner);
      }

      this.drawerMovedToBody = false;
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    document.body.classList.remove("overflow-hidden");
    if (event === undefined) return;

    // Remove any submenu focus trap
    this.removeSubmenuFocusTrap();

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.closeAnimation(this.mainDetailsToggle);
    const submenuElement = document.querySelector(
      ".offcanvas__menu .submenu-open"
    );

    if (submenuElement) {
      submenuElement.classList.remove("submenu-open");
    }

    // Mobile drawer redesign: Animate out first, then move drawer elements back
    if (this.isMobileDrawerRedesign && this.drawerMovedToBody) {
      // Remove focus trap before closing
      this.removeFocusTrap();

      // Remove visible classes to trigger close animation
      this.drawerOverlay.classList.remove('drawer__overlay--visible');
      this.drawerInner.classList.remove('offcanvas__inner--visible');

      // Delay moving back to allow close animation to complete
      setTimeout(() => {
        this.moveDrawerBack();
      }, 450);
    } else {
      // For non-redesign mode, just remove focus trap
      this.removeFocusTrap();
    }
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    // Remove submenu focus trap before closing
    this.removeSubmenuFocusTrap();

    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");

    const summaryElement = detailsElement.querySelector("summary");
    summaryElement.setAttribute("aria-expanded", false);

    this.closeAnimation(detailsElement);

    // Return focus to the summary element that opened this submenu
    setTimeout(() => {
      summaryElement.focus();
    }, 100);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  connectedCallback() {
    // Theme editor: Handle section reload to properly update drawer content
    if (window.Shopify && window.Shopify.designMode) {
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload.bind(this));
    }
  }

  disconnectedCallback() {
    // Clean up document-level event listener
    if (this.boundOnKeyUp) {
      document.removeEventListener("keyup", this.boundOnKeyUp);
    }

    if (window.Shopify && window.Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    }
  }

  handleSectionUnload(event) {
    // When section is about to be replaced, clean up moved elements
    if (this.isMobileDrawerRedesign && this.drawerMovedToBody) {
      // Remove the drawer elements from body before section is destroyed
      if (this.drawerOverlay && this.drawerOverlay.parentNode === document.body) {
        this.drawerOverlay.remove();
      }
      if (this.drawerInner && this.drawerInner.parentNode === document.body) {
        this.drawerInner.remove();
      }
      this.drawerMovedToBody = false;
    }
  }

  handleSectionLoad(event) {
    // After section reload, close any open drawers and reset state
    const newOffcanvasNav = document.querySelector('offcanvas-nav.mobile-drawer-redesign');
    if (newOffcanvasNav) {
      // Ensure drawer is closed and elements are in proper position
      const mainDetails = newOffcanvasNav.querySelector('details');
      if (mainDetails) {
        mainDetails.removeAttribute('open');
        mainDetails.classList.remove('menu-opening');
      }
      document.body.classList.remove('overflow-hidden');
    }
  }
}

customElements.define("offcanvas-nav", OffcanvasNav);
